import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode, Ref } from 'react'
import { CalendarIcon } from '@phosphor-icons/react'
import { format } from 'date-fns'
import type { Matcher } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DateTimeSection } from '@/components/shared/fields/DateTimeSection'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { cn } from '@/lib/utils'
import { DATE_FORMATS, getDateOnlyForDisplay } from '@/utils/date'
import {
  formatMaskedDate,
  getMaskedCursorPosition,
  maskDateInput,
  parseMaskedDate,
} from '@/utils/date-input'

export interface DatePreset {
  label: string
  getDate: () => Date
}

const DEFAULT_PRESETS: DatePreset[] = [
  { label: 'Today', getDate: () => new Date() },
  {
    label: 'Tomorrow',
    getDate: () => {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      return d
    },
  },
  {
    label: '+1 Week',
    getDate: () => {
      const d = new Date()
      d.setDate(d.getDate() + 7)
      return d
    },
  },
]

export interface DatePickerPanelProps {
  value: Date | null
  /** Fires on calendar pick, valid typed Enter/blur, preset click, and clear (null). */
  onCommit: (date: Date | null) => void
  /**
   * Treat the value as a calendar date (no timezone shifting). Callers convert
   * to/from storage with toDateOnlyString/createDateOnlyUTC at the boundary.
   */
  dateOnly?: boolean
  /** true = Today / Tomorrow / +1 Week; or pass a custom list. */
  presets?: boolean | DatePreset[]
  clearable?: boolean
  disabledDates?: Matcher | Matcher[]
  fromDate?: Date
  toDate?: Date
  defaultMonth?: Date
  /** react-day-picker day modifiers (e.g. highlight days with data). */
  modifiers?: Record<string, Matcher | Matcher[]>
  modifiersClassNames?: Record<string, string>
  /** Rendered between the calendar and the clear row (recurring editor, custom time UI). */
  extraSections?: ReactNode
  // Optional time-of-day section
  showTimeSection?: boolean
  hasTime?: boolean
  onTimeChange?: (nextDate: Date, nextHasTime: boolean) => void
  inputStyle?: CSSProperties
  autoFocusInput?: boolean
  disabled?: boolean
}

function toDisplayDate(value: Date | null, dateOnly: boolean): Date | null {
  if (!value) return null
  if (!dateOnly) return value
  return getDateOnlyForDisplay(value)
}

/**
 * The shared date picker popover content: optional presets, a masked
 * MM/DD/YYYY input, an inline calendar, optional extra sections, clear, and an
 * optional time-of-day editor. Sections render only when enabled, so simple
 * call sites stay simple and rich ones lose nothing.
 */
export function DatePickerPanel({
  value,
  onCommit,
  dateOnly = true,
  presets = false,
  clearable = true,
  disabledDates,
  fromDate,
  toDate,
  defaultMonth,
  modifiers,
  modifiersClassNames,
  extraSections,
  showTimeSection = false,
  hasTime = false,
  onTimeChange,
  inputStyle,
  autoFocusInput = true,
  disabled = false,
}: DatePickerPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const displayDate = toDisplayDate(value, dateOnly)

  // Radix focuses the popover content after mount, which beats the input's
  // autoFocus attribute — reclaim focus a frame later so typing lands here.
  useEffect(() => {
    if (!autoFocusInput) return
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [textValue, setTextValue] = useState(() =>
    displayDate ? formatMaskedDate(displayDate) : '',
  )

  // Keep the input in sync when the value changes externally (calendar pick,
  // preset, remote update).
  const displayKey = displayDate ? displayDate.toDateString() : ''
  useEffect(() => {
    setTextValue(displayDate ? formatMaskedDate(displayDate) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayKey])

  const commitTyped = (): boolean => {
    const trimmed = textValue.trim()
    if (!trimmed) {
      if (value) onCommit(null)
      return true
    }
    const parsed = parseMaskedDate(trimmed)
    if (parsed) {
      onCommit(parsed)
      return true
    }
    // Invalid input: restore the current value's text
    setTextValue(displayDate ? formatMaskedDate(displayDate) : '')
    return false
  }

  const presetList: DatePreset[] =
    presets === true ? DEFAULT_PRESETS : presets || []

  return (
    <div className="flex flex-col">
      {presetList.length > 0 && (
        <div className="flex gap-2 px-3 pt-3">
          {presetList.map(preset => (
            <Button
              key={preset.label}
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onCommit(preset.getDate())}
              className="h-8 flex-1"
            >
              {preset.label}
            </Button>
          ))}
        </div>
      )}

      <div className="px-3 pt-3">
        <Input
          ref={inputRef}
          type="text"
          placeholder="MM/DD/YYYY"
          maxLength={10}
          value={textValue}
          disabled={disabled}
          style={inputStyle}
          onChange={e => {
            const raw = e.target.value
            const cursor = e.target.selectionStart || 0
            const masked = maskDateInput(raw)
            const oldValue = textValue
            setTextValue(masked)
            requestAnimationFrame(() => {
              if (inputRef.current) {
                const pos = getMaskedCursorPosition(oldValue, raw, masked, cursor)
                inputRef.current.setSelectionRange(pos, pos)
              }
            })
          }}
          onBlur={e => {
            // Ignore blur into the rest of the panel (calendar, presets)
            const related = e.relatedTarget as HTMLElement | null
            if (related && e.currentTarget.closest('[data-date-picker-panel]')?.contains(related)) {
              return
            }
            commitTyped()
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitTyped()
            }
          }}
          className="h-9"
        />
      </div>

      <Calendar
        mode="single"
        selected={displayDate ?? undefined}
        defaultMonth={displayDate ?? defaultMonth}
        disabled={disabled ? true : disabledDates}
        fromDate={fromDate}
        toDate={toDate}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        onSelect={date => {
          if (date) onCommit(date)
        }}
      />

      {extraSections}

      {clearable && value && (
        <div className="border-t px-3 py-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => onCommit(null)}
            className="h-8 w-full text-muted-foreground hover:text-destructive"
          >
            Clear date
          </Button>
        </div>
      )}

      {showTimeSection && onTimeChange && (
        <div className="border-t px-3 py-2">
          <DateTimeSection
            date={displayDate}
            hasTime={hasTime}
            disabled={disabled}
            onTimeChange={onTimeChange}
          />
        </div>
      )}
    </div>
  )
}

export interface DateInputPickerProps
  extends Omit<DatePickerPanelProps, 'onCommit' | 'autoFocusInput' | 'value'> {
  value: Date | null | undefined
  onChange: (date: Date | null) => void
  /** Called after each commit and on popover close (react-hook-form timing). */
  onBlur?: () => void
  placeholder?: string
  'aria-invalid'?: boolean
  /** 'button' = form-field sizing; 'compact' = filter-bar sizing. */
  triggerVariant?: 'button' | 'compact'
  /** Escape hatch: fully custom trigger (rendered via asChild). */
  trigger?: ReactNode
  triggerClassName?: string
  /** Inline style for the trigger button (form branding passthrough). */
  triggerStyle?: CSSProperties
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  id?: string
  formatTriggerLabel?: (date: Date) => string
  ref?: Ref<HTMLButtonElement>
  /** Injected by shadcn FormControl's Slot; forwarded to the trigger button. */
  'aria-describedby'?: string
}

/**
 * The app-standard date picker: a trigger showing the selected date as
 * "Jun 2, 2026" and a DatePickerPanel popover. Drops into react-hook-form via
 * value/onChange/onBlur/ref/aria-invalid.
 */
export function DateInputPicker({
  onChange,
  onBlur,
  placeholder = 'Select date',
  'aria-invalid': ariaInvalid,
  triggerVariant = 'button',
  trigger,
  triggerClassName,
  triggerStyle,
  className,
  open: openProp,
  onOpenChange,
  id,
  formatTriggerLabel,
  ref,
  value: valueProp,
  dateOnly = true,
  disabled = false,
  'aria-describedby': ariaDescribedBy,
  ...panelProps
}: DateInputPickerProps) {
  const value = valueProp ?? null
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openProp ?? internalOpen
  const setOpen = (next: boolean) => {
    setInternalOpen(next)
    onOpenChange?.(next)
    if (!next) onBlur?.()
  }

  const { formatDate } = useUserTimezone()

  const displayDate = toDisplayDate(value, dateOnly)
  const triggerLabel = displayDate
    ? formatTriggerLabel
      ? formatTriggerLabel(displayDate)
      : dateOnly
        ? format(displayDate, DATE_FORMATS.display)
        : formatDate(displayDate, DATE_FORMATS.display)
    : null

  const handleCommit = (date: Date | null) => {
    onChange(date)
    setOpen(false)
    onBlur?.()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <Button
            ref={ref}
            id={id}
            type="button"
            variant="outline"
            size={triggerVariant === 'compact' ? 'sm' : 'default'}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            style={triggerStyle}
            className={cn(
              'justify-start text-left font-normal',
              triggerVariant === 'button' && 'w-full',
              triggerVariant === 'compact' && 'h-8',
              !value && 'text-muted-foreground',
              ariaInvalid && 'border-destructive focus-visible:ring-destructive',
              triggerClassName,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            {triggerLabel ?? placeholder}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-auto p-0', className)}
        align="start"
        data-date-picker-panel
      >
        <DatePickerPanel
          value={value}
          dateOnly={dateOnly}
          disabled={disabled}
          onCommit={handleCommit}
          {...panelProps}
        />
      </PopoverContent>
    </Popover>
  )
}
